import React from 'react';
import { Heading, Box, AccordionItem, Button } from 'components';
import { observer } from 'mobx-react';
import { Warning } from 'icons';
import { LargePanelSectionWrapper } from 'libs/rendering/gltf/components/largePanel/styles';
import useFileInput from 'libs/useFileInput';
import { fileFormatsToReadableText } from 'apps/rfq/fileRequirementsUtils';
import { SupportingFileIssue } from './SupportingFileIssue';

interface RFQIssuesProps {
  rfqManager: any;
}

export const RFQIssues = observer(function RFQIssues({
  rfqManager,
}: RFQIssuesProps) {
  const { fileRequirements, state } = rfqManager.rfqApp;
  const isAddressMissing = !rfqManager.rfqApp.address;

  let title = null;
  let message = null;
  let fileFormatRequirements = [];
  let supportingFilesRequirements = [];
  if (fileRequirements) {
    fileFormatRequirements = fileRequirements.filter(
      (fReq: any) => fReq.type === 'FILE_FORMAT',
    );
    supportingFilesRequirements = fileRequirements.filter(
      (fReq: any) => fReq.type === 'SUPPORTING_FILE',
    );
  }

  let acceptPrimaryFileFormats = '';
  if (fileFormatRequirements.length > 0) {
    acceptPrimaryFileFormats = fileFormatRequirements[0].formats.join(',');
  }
  const selectPrimaryFile = useFileInput(
    (fileList: any) => {
      if (fileList.length > 0) rfqManager.UpdatePrimaryFile(fileList[0]);
    },
    { multiple: false, accept: acceptPrimaryFileFormats },
  );

  const isRFQIncomplete = state === 'INCOMPLETE';

  if (!isRFQIncomplete) return null;

  if (fileFormatRequirements.length > 0) {
    // NOTE (david): There should ever be at most 1 FILE_FORMAT requirements.
    const fileFormatRequirement = fileFormatRequirements[0];
    const formatsText = fileFormatsToReadableText(
      fileFormatRequirement.formats,
    );
    const forText = fileFormatRequirement.for.name;

    title = `${formatsText} File Required for your RFQ`;
    message = `The file format you uploaded as your primary RFQ file is not valid for ${forText}. Please upload a ${formatsText} file.`;
  }

  return (
    <LargePanelSectionWrapper>
      <Box>
        <Heading level={2} weight='xbold' size='large'>
          RFQ Issues
        </Heading>

        {isAddressMissing && (
          <Box mb={4}>
            <AccordionItem
              icon={<Warning />}
              divisor
              title='Delivery Address required'
              content='Please provide a delivery address so that we can include shipping costs in your final quote.'
              button={
                <Button
                  text='Add Address'
                  icon='add'
                  iconPosition='right'
                  variant='tertiary'
                  size='small'
                  outline
                  onClick={() => rfqManager.BeginChangeAddress()}
                />
              }
            />
          </Box>
        )}

        {fileFormatRequirements.length > 0 && (
          <Box mb={4}>
            <AccordionItem
              icon={<Warning />}
              divisor
              title={title}
              content={message}
              button={
                <Button
                  text='Upload File'
                  icon='upload'
                  iconPosition='right'
                  variant='tertiary'
                  size='small'
                  outline
                  onClick={selectPrimaryFile}
                  data-testid='upload-primary-file-requirement-button'
                />
              }
            />
          </Box>
        )}
        {supportingFilesRequirements.map((supportingFileRequirement: any) => (
          <SupportingFileIssue
            key={supportingFileRequirement.id}
            supportingFileRequirement={supportingFileRequirement}
            rfqManager={rfqManager}
          />
        ))}
      </Box>
    </LargePanelSectionWrapper>
  );
});
